import Icon from '@atoms/Icon/Icon';
import type { TabbedFlipboardTextCardType } from '@models/ITabbedFlipboard';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import usePreviewMode from '@utils/hooks/usePreviewMode';
import classNames from 'classnames';

interface Props extends Omit<TabbedFlipboardTextCardType, 'type'> {
  readonly dataTestId: string;
  readonly isSmall?: boolean;
}

const TabbedFlipboardTextCard = ({
  stat,
  icon,
  headline,
  body,
  meta,
  dataTestId,
  isSmall,
}: Props) => {
  const isMobile = useMediaQuery(1024);
  const isPreview = usePreviewMode();

  return (
    <div
      className={classNames(
        'relative flex h-full w-full flex-col justify-between overflow-hidden rounded-lg bg-grey-200 text-black-100 dark:bg-grey-600 dark:text-white',
        {
          'max-h-[540px] max-w-[540px]': isPreview,
        }
      )}
      data-testid={dataTestId}
    >
      <div
        className={classNames(
          'px-4 pt-4 lg:pt-6',
          isSmall ? 'lg:px-7' : 'lg:px-7.5'
        )}
      >
        {stat && (
          <p
            className={isSmall || isMobile ? 'text-7xl leading-66' : 'strap'}
            data-testid={`${dataTestId}-Stat`}
            data-epi-property-name={meta?.editHints?.stat}
          >
            {stat}
          </p>
        )}
        {icon && (
          <div
            data-testid={`${dataTestId}-Icon`}
            data-epi-property-name={meta?.editHints?.icon}
            aria-hidden="true"
          >
            <Icon
              name={icon}
              size={isSmall || isMobile ? '66px' : '110px'}
              colorClassName="text-black-100 dark:text-white"
            />
          </div>
        )}
      </div>

      <div
        className={classNames(
          'flex flex-col gap-y-2 px-4 pb-4 lg:gap-y-7 lg:pb-5',
          isSmall ? 'lg:w-11/12 lg:px-7' : 'lg:px-7.5'
        )}
      >
        {headline && (
          <p
            className="h3"
            data-testid={`${dataTestId}-Headline`}
            data-epi-property-name={meta?.editHints?.headline}
          >
            {headline}
          </p>
        )}
        <p
          className={isSmall || isMobile ? 'body-two' : 'h6'}
          data-testid={`${dataTestId}-Body`}
          data-epi-property-name={meta?.editHints?.body}
        >
          {body}
        </p>
      </div>
    </div>
  );
};

export default TabbedFlipboardTextCard;
